<template>
  <div class="row">
    <div class="col-lg-2 mb-3">
      <div class="tab-title-divider d-md-block d-lg-block d-xl-block d-none d-sm-none">
        <span class='tab-title'>Properties</span>
        <router-link class="float-right tab-add form-action-text text-secondary white-space-nowrap"
          :to="{ name: 'newProperty', params: {
          project_id: $route.params.project_id}}"
        >
          + Add
        </router-link>
      </div>
      <LoadingSpinner v-if="propertiesLoading" message ='Loading Properties' size = '1x'>
      </LoadingSpinner>
      <!-- eslint-disable-next-line max-len -->
      <router-link class="btn btn-block btn-secondary btn-sm text-white mb-3 d-md-none d-lg-none d-xl-none d-block d-sm-block white-space-nowrap"
        :to="{ name: 'newProperty', params: {
          project_id: $route.params.project_id}}"
          tag="button">
        + Add
      </router-link>
      <ul class="nav nav-pills flex-column mb-3" v-if="!propertiesLoading">
        <li v-for='property in orderedProperties'
          :key='property.id'
          class="nav-item">
          <router-link
            class="nav-link text-left py-1 px-2"
            :to="{ name: 'projectProperty', params: {
                    project_id: $route.params.project_id,
                    property_id: property.id }}">
            <div class="d-flex flex-column">
              <div class="d-flex justify-content-between">
                <div>
                  {{property.id}}
                </div>
                <font-awesome-icon
                  :id="'prop-' + property.id"
                  icon="circle"
                  class="rounded-circle border border-white border-circle"
                  size="sm"
                  :aria-label="`Status: ${getStatusName(property.statusId)}`"
                  :class="getStatusClass(property.statusId, 'Property')" />
                <b-tooltip :target="'prop-' + property.id">
                  {{getStatusName(property.statusId)}}
                </b-Tooltip>
              </div>
              <small>
                {{ property.calculatedAcres | acresFormatNullIsNA }} acres
              </small>
            </div>
          </router-link>
        </li>
      </ul>
    </div>
    <div :key="currentProperty.id" class="col-lg-10">
      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{ serverError }}
      </div>
      <div class="text-right expand-collapse">
        <b-link class="text-secondary" @click="expandAll">Expand All</b-link> |
        <b-link class="text-secondary" @click="collapseAll">Collapse All</b-link>
      </div>
      <ViewProperty v-if="!isPropertyEdit"
        ref="viewPropertyCard">
      </ViewProperty>
      <EditPropertyForm
        v-if="isPropertyEdit"
        ref="propertyDetails"
      >
      </EditPropertyForm>
      <MapCard
        :defaultOpened=false
        :baseUrl="mapUrl"
        :featureId="currentProperty.id"
        title="Property"
        ref="mapCard"
      >
      </MapCard>
      <AssociatedInstrumentsCard
        :entities="associatedInstruments"
        ref="associatedInstrumentsCard"
      >
      </AssociatedInstrumentsCard>
      <LocalitiesCard
        :propertyId="currentProperty.id"
        ref="localitiesCard"
      >
      </LocalitiesCard>
      <BuiltEnvironmentFeatureCard
        :parentId="currentProperty.id"
        :parentType="'property'"
        :parentTitle="'Property'"
        ref="builtEnvCard"
      >
      </BuiltEnvironmentFeatureCard>
      <AssociatedServicesCard
        :entities="associatedServices"
        ref="associatedServicesCard"
      >
      </AssociatedServicesCard>
      <ContactsCard
        :parentId="currentProperty.id"
        :parentType="'property'"
        :parentTitle="'Property'"
        :roles="contactRoles"
        :rolesLoading="rolesLoading"
        :entities="contacts"
        :entitiesLoading="contactsLoading"
        ref="contactsCard"
      >
      </ContactsCard>
      <BoxCard
        :folderId="currentProperty.documentFolderId"
        :parentId="currentProperty.id"
        storeNamespace="Property"
        ref="boxCard"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AssociatedInstrumentsCard from '../components/AssociatedInstrumentsCard.vue';
import AssociatedServicesCard from '../components/AssociatedServicesCard.vue';
import BoxCard from '../components/common/BoxCard.vue';
import BuiltEnvironmentFeatureCard from '../components/builtEnvironmentFeature/BuiltEnvironmentFeatureCard.vue';
import ContactsCard from '../components/contacts/ContactsCard.vue';
import EditPropertyForm from '../components/property/EditPropertyForm.vue';
import MapCard from '@/components/common/MapCard.vue';
import LocalitiesCard from '../components/localities/LocalitiesCard.vue';
import ViewProperty from '../components/property/ViewProperty.vue';
import { EntityModules, LookupModules } from '@/store/moduleConfig';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import RouterCheck from '@/mixins/RouterCheck';
import StatusHelper from '../mixins/StatusHelper';

export default {
  name: 'ProjectProperties',
  mixins: [RouterCheck, StatusHelper],
  components: {
    AssociatedInstrumentsCard,
    AssociatedServicesCard,
    BoxCard,
    BuiltEnvironmentFeatureCard,
    ContactsCard,
    EditPropertyForm,
    LocalitiesCard,
    MapCard,
    ViewProperty,
    LoadingSpinner,
  },
  created() {
    // dispatch actions to populate vuex state for this page
    this.$store.dispatch('Property/fetchProperties', parseInt(this.$route.params.project_id, 10))
      .then(() => {
        if (this.$route.name === 'projectProperties') {
          this.pushDefaultRoute();
        } else {
          this.dispatchToFetchPropertyAndChildren();
        }
      });
    this.dispatchToFetchLookups();
  },
  computed: {
    ...mapGetters('Property', ['orderedProperties', 'getDefaultProperty']),
    ...mapState('Property', {
      propertiesLoading: state => state.propertiesLoading,
      isPropertyEdit: state => state.isPropertyEdit,
      currentProperty: state => state.currentProperty,
      serverError: state => state.serverError,
    }),
    ...mapState(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.PROPERTY_ROLES.namespace, {
      rolesLoading: state => state.loading,
    }),
    ...mapState({ mapUrl: state => state.Config.config.agolPropertyTemplateUrl }),
    ...mapState(EntityModules.PROPERTY_CONTACT.namespace, {
      contacts: state => state.entities,
      contactsLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.PROPERTY_STATUSES.namespace, {
      propertyStatusOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.PROPERTY_ROLES.namespace, {
      contactRoles: 'getActiveOptions',
    }),
    associatedInstruments() {
      if (this.currentProperty.propertyInstruments) {
        return this.currentProperty.propertyInstruments.map(pi => pi.instrument);
      }
      return [];
    },
    associatedServices() {
      if (this.currentProperty.propertyServices) {
        return this.currentProperty.propertyServices.map(ps => ps.service);
      }
      return [];
    },
  },
  watch: {
    $route(to) {
      if (to.name === 'projectProperties') {
        this.pushDefaultRoute();
      } else {
        this.dispatchToFetchPropertyAndChildren();
      }
    },
  },
  filters: {
    propertyNavString(property) {
      let acresString = '';
      if (property.calculatedAcres) {
        acresString = ` - ${property.calculatedAcres.toLocaleString('en-US', { maximumFractionDigits: 4 })} Acres`;
      }
      return `${property.id}${acresString}`;
    },
  },
  methods: {
    getCardRefs() {
      const refs = [
        this.$refs.mapCard,
        this.$refs.associatedInstrumentsCard,
        this.$refs.localitiesCard,
        this.$refs.builtEnvCard,
        this.$refs.associatedServicesCard,
        this.$refs.contactsCard,
        this.$refs.boxCard,
      ];
      if (this.isPropertyEdit) {
        refs.push(this.$refs.propertyDetails);
      } else {
        refs.push(this.$refs.viewPropertyCard);
      }
      return refs;
    },
    expandAll() {
      this.getCardRefs().forEach((ref) => {
        ref.forceExpand();
      });
    },
    collapseAll() {
      this.getCardRefs().forEach((ref) => {
        ref.forceCollapse();
      });
    },
    getStatusName(id) {
      const filteredStatuses = this.propertyStatusOptions.filter(status => status.id === id);
      if (filteredStatuses && filteredStatuses[0]) {
        return filteredStatuses[0].name;
      }
      return '';
    },
    pushDefaultRoute() {
      this.$router.push({
        name: 'projectProperty',
        params: {
          project_id: parseInt(this.$route.params.project_id, 10),
          property_id: parseInt(this.getDefaultProperty.id, 10),
        },
      });
    },
    dispatchToFetchPropertyAndChildren() {
      this.$store.dispatch('Property/fetchProperty',
        {
          projectId: parseInt(this.$route.params.project_id, 10),
          id: parseInt(this.$route.params.property_id, 10),
        })
        .then(() => {
          // the following are only being loaded in this clause to ensure they fire
          // after the property has been retrieved
          this.$store.dispatch(`${EntityModules.PROPERTY_CONTACT.namespace}/fetchEntities`, {
            parentType: EntityModules.PROPERTY_CONTACT.parentType,
            parentId: this.currentProperty.id,
            entityType: EntityModules.PROPERTY_CONTACT.entityType,
          });
          this.$store.dispatch(
            `${EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace}/fetchEntities`, {
              parentType: EntityModules.BUILT_ENVIRONMENT_FEATURE.parentType,
              parentId: this.currentProperty.id,
              entityType: EntityModules.BUILT_ENVIRONMENT_FEATURE.entityType,
            },
          );
          this.$store.dispatch(`${EntityModules.LOCALITY.namespace}/fetchEntities`, {
            parentType: EntityModules.LOCALITY.parentType,
            parentId: this.currentProperty.id,
            entityType: EntityModules.LOCALITY.entityType,
          });
          this.$store.dispatch(`${EntityModules.ASSOCIATED_INSTRUMENTS.namespace}/fetchEntities`, {
            parentType: EntityModules.ASSOCIATED_INSTRUMENTS.parentType,
            parentId: this.currentProperty.id,
            entityType: EntityModules.ASSOCIATED_INSTRUMENTS.entityType,
          });
          this.$store.dispatch(`${EntityModules.ASSOCIATED_SERVICES.namespace}/fetchEntities`, {
            parentType: EntityModules.ASSOCIATED_SERVICES.parentType,
            parentId: this.currentProperty.id,
            entityType: EntityModules.ASSOCIATED_SERVICES.entityType,
          });
        });
    },
    dispatchToFetchLookups() {
      this.$store.dispatch(`${LookupModules.PROPERTY_STATUSES.namespace}/fetchOptions`,
        LookupModules.PROPERTY_STATUSES.url);
      this.$store.dispatch(`${LookupModules.AGREEMENT_TYPES.namespace}/fetchOptions`,
        LookupModules.AGREEMENT_TYPES.url);
      this.$store.dispatch(`${LookupModules.PROPERTY_ROLES.namespace}/fetchOptions`,
        LookupModules.PROPERTY_ROLES.url);
      this.$store.dispatch(`${LookupModules.SERVICE_STATUSES.namespace}/fetchOptions`,
        LookupModules.SERVICE_STATUSES.url);
      this.$store.dispatch(`${LookupModules.SERVICE_CATEGORIES.namespace}/fetchOptions`,
        LookupModules.SERVICE_CATEGORIES.url);
      this.$store.dispatch('fetchConfiguration')
        .then(() => console.log(`retrieve ${this.mapUrl}`));
    },
  },
};
</script>
